import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, Mail, AlertTriangle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

// Smart backend URL detection - prioritizes runtime domain detection for production
const getBackendUrl = () => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    
    // For custom production domains (like app.outsiderstravel.gr), use same origin
    if (!hostname.includes('preview.emergentagent.com') && 
        !hostname.includes('emergent.host') && 
        !hostname.includes('localhost') &&
        !hostname.includes('127.0.0.1')) {
      return window.location.origin;
    }
  }
  
  // For Emergent preview environments or local development, use the env variable
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  
  return '';
};

const BACKEND_URL = getBackendUrl();

export default function EmergencyReset() {
  const [email, setEmail] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/app/emergency-password-reset?email=${encodeURIComponent(email)}&secret_key=${encodeURIComponent(secretKey)}`,
        { method: 'POST' }
      );
      
      const data = await response.json();
      
      if (response.ok) {
        setResult({
          success: true,
          password: data.temporary_password,
          message: data.message
        });
        toast.success('Password reset successful!');
      } else {
        setResult({
          success: false,
          message: data.detail || 'Reset failed'
        });
        toast.error(data.detail || 'Reset failed');
      }
    } catch (error) {
      setResult({
        success: false,
        message: 'Network error. Please try again.'
      });
      toast.error('Network error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-amber-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800">Emergency Password Reset</h1>
            <p className="text-slate-500 mt-2">Reset your password after domain migration</p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <div className="flex gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">Security Notice</p>
                <p>This page requires a secret key. Contact your administrator if you don't have it.</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleReset} className="space-y-4">
            <div>
              <Label htmlFor="email">Email Address</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="secretKey">Secret Key</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="secretKey"
                  type="password"
                  placeholder="Enter secret key"
                  value={secretKey}
                  onChange={(e) => setSecretKey(e.target.value)}
                  className="pl-10"
                  required
                  noUpperCase={true}
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-amber-600 hover:bg-amber-700"
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </form>

          {result && (
            <div className={`mt-6 p-4 rounded-lg ${result.success ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
              {result.success ? (
                <div>
                  <div className="flex items-center gap-2 text-emerald-700 font-medium mb-2">
                    <CheckCircle className="w-5 h-5" />
                    Password Reset Successful!
                  </div>
                  <p className="text-sm text-emerald-600 mb-3">Your temporary password is:</p>
                  <div className="bg-white border border-emerald-300 rounded-lg p-3 font-mono text-lg text-center select-all">
                    {result.password}
                  </div>
                  <p className="text-xs text-emerald-600 mt-3">
                    Copy this password and use it to login. Then change it in Settings.
                  </p>
                </div>
              ) : (
                <div className="text-red-700">
                  <p className="font-medium">Reset Failed</p>
                  <p className="text-sm">{result.message}</p>
                </div>
              )}
            </div>
          )}

          <div className="mt-6 text-center">
            <a href="/login" className="text-sm text-blue-600 hover:underline">
              ← Back to Login
            </a>
          </div>
          
          {/* Debug info */}
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-400">
              API: {BACKEND_URL || window.location.origin}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
